import { ArrowLeft, ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n.jsx";

/* ------------------------------------------------------------------
   GoalTimeline
   ------------------------------------------------------------------
   Compact horizontal strip below the weight chart summarising every
   goal-weight change over time (oldest → newest). Each step shows the
   date + the goal that was set on that date. The very last chip is the
   CURRENT goal, highlighted in orange.
------------------------------------------------------------------- */
export default function GoalTimeline({ goalHistory, currentGoal }) {
  const { lang } = useI18n();
  const en = lang === "en";
  // Reading order: in RTL (AR) oldest sits on the RIGHT and time flows
  // left; in LTR (EN) oldest sits on the LEFT and time flows right.
  // Arrows always point in the direction of time flow.
  const sorted = [...(goalHistory || [])].sort(
    (a, b) => (a.effective_from || "").localeCompare(b.effective_from || "")
  );
  if (sorted.length === 0) return null;

  const AR_MONTHS = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
  const EN_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const fmt = (iso) => {
    if (!iso) return "";
    const [, m, d] = iso.split("-").map((n) => parseInt(n, 10));
    return en ? `${EN_MONTHS[m - 1]} ${d}` : `${d} ${AR_MONTHS[m - 1]}`;
  };

  const Arrow = en ? ArrowRight : ArrowLeft;

  return (
    <div className="mt-3" dir={en ? "ltr" : "rtl"} data-testid="goal-timeline">
      <div className="text-[11px] font-semibold text-neutral-500 mb-2">
        {en ? "Goal timeline" : "مسار الأهداف"}
      </div>
      <div className="overflow-x-auto -mx-2 px-2">
        <div className="flex items-stretch gap-2 min-w-max pb-1">
          {sorted.map((g, i) => {
            const isCurrent = i === sorted.length - 1
              && Math.abs((g.target_weight_kg || 0) - (currentGoal || 0)) < 0.1;
            const isLast = i === sorted.length - 1;
            return (
              <div key={g.id || i} className="flex items-stretch gap-2">
                <div
                  className={`flex flex-col items-center justify-center px-3 py-1.5 rounded-xl border ${
                    isCurrent
                      ? "bg-brand-500 border-brand-500 text-white"
                      : "bg-neutral-50 border-neutral-200 text-neutral-700"
                  }`}
                  data-testid={`goal-timeline-item-${i}`}
                >
                  <div className={`text-sm font-black num leading-none ${isCurrent ? "text-white" : "text-neutral-900"}`}>
                    {(g.target_weight_kg || 0).toFixed(1)}
                    <span className={`text-[9px] font-semibold ${en ? "ml-0.5" : "mr-0.5"} ${isCurrent ? "text-white/80" : "text-neutral-500"}`}>
                      {en ? "kg" : "كجم"}
                    </span>
                  </div>
                  <div className={`text-[9px] mt-0.5 ${isCurrent ? "text-white/85" : "text-neutral-500"}`}>
                    {fmt(g.effective_from)}
                  </div>
                </div>
                {!isLast && (
                  <div className="flex items-center px-0.5">
                    <Arrow className="w-5 h-5 text-brand-400" strokeWidth={2.5} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
